import { safeJson } from "src/app/helpers/Index";
import { faArrowsRotate, faDisplay, faLink, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React from "react";
import { BoardService } from "src/app/services/BoardService";
import { ScoreboardCommandService } from "src/app/services/ScoreboardCommandService";
import { ScoreboardConfigService } from "src/app/services/ScoreboardConfigService";
import { ScoreboardStateService } from "src/app/services/ScoreboardStateService";
import { FixtureService, ScoreboardService } from "src/app/services/Index";
import { BoardPreview } from "src/app/components/BoardPreview";
import { ClubContext } from "src/app/helpers/ClubContext";
import { environment } from "src/environments/environment";
import "./AdminBoards.css";

type Board = {
	id: number;
	name: string;
	paired: boolean;
	lastSeenAt: string | null;
	appVersion?: string | null;
	scoreboardConfigId?: number | null;
	online?: boolean;
};

type Release = { version: string | null; url: string | null };

type Props = {};
type State = {
	boards: Board[];
	isLoading: boolean;
	showAdd: boolean;
	pairingCode: string;
	newName: string;
	saving: boolean;
	configs: any[];
	states: { [boardId: number]: any };
	fixture: any;
	themes: { [configId: number]: any };
	refreshing: { [boardId: number]: boolean };
	latestRelease: Release | null;
	updating: { [boardId: number]: boolean };
};

/**
 * The club's registered scoreboards. A board shows a pairing code on first run;
 * entering it here claims the board for this club and names it.
 */
export class AdminBoards extends React.Component<Props, State> {
	toast: any;
	refreshTimer: any;

	constructor(props: Props) {
		super(props);
		this.state = { boards: [], isLoading: true, showAdd: false, pairingCode: "", newName: "", saving: false, configs: [], states: {}, fixture: null, themes: {}, refreshing: {}, latestRelease: null, updating: {} };
	}

	componentDidMount() {
		this.getBoards();
		this.getConfigs();
		this.getFixture();
		this.getLatestRelease();
		this.getThemes();
		// Boards report in as they connect, and their state changes as the
		// operator works; keep the list and thumbnails reasonably fresh.
		this.refreshTimer = setInterval(() => { this.getBoards(); this.getFixture(); }, 10000);
	}

	componentWillUnmount() {
		clearInterval(this.refreshTimer);
	}

	async getBoards() {
		try {
			const res = await safeJson(BoardService.getMine());
			if (Array.isArray(res)) {
				this.setState({ boards: res, isLoading: false }, () => this.getThemes());
				res.forEach((board: Board) => this.getBoardState(board.id));
			} else this.setState({ isLoading: false });
		} catch {
			this.setState({ isLoading: false });
		}
	}

	/** Re-read one board's state (and the scoreline) on demand. */
	async refreshPreview(boardId: number) {
		this.setState({ refreshing: { ...this.state.refreshing, [boardId]: true } });
		await Promise.all([this.getBoardState(boardId), this.getFixture()]);
		// Brief minimum spin so the button visibly acknowledges the tap.
		setTimeout(() => this.setState({ refreshing: { ...this.state.refreshing, [boardId]: false } }), 400);
	}

	/** What one board is showing right now, from its own state document. */
	async getBoardState(boardId: number) {
		try {
			const res = await safeJson(ScoreboardStateService.getStateForBoard(boardId));
			if (res && !res.error) this.setState({ states: { ...this.state.states, [boardId]: res } });
		} catch { /* pre-registry API */ }
	}

	/** The club's fixture, so the live-board preview can show the scoreline. */
	async getFixture() {
		try {
			const res = await safeJson(FixtureService.getLatest());
			if (res && !res.error) this.setState({ fixture: res });
		} catch { /* offline */ }
	}

	/**
	 * Each layout in use, so a board's thumbnail is drawn with the same
	 * geometry and assets that board actually renders with. Keyed by config
	 * id, with 0 for the club default.
	 */
	async getThemes() {
		const wanted = new Set<number>([0, ...this.state.boards.map((b) => b.scoreboardConfigId || 0)]);
		for (const configId of Array.from(wanted)) {
			if (this.state.themes[configId]) continue;
			try {
				const res = await safeJson(ScoreboardService.getTheme(configId || undefined));
				if (res && !res.error) this.setState({ themes: { ...this.state.themes, [configId]: res } });
			} catch { /* offline */ }
		}
	}

	/** Existing scoreboard web routes, reused for the named-screen thumbnails. */
	screenRoute(screen: string) {
		const routes: { [key: string]: string } = {
			"Latest": "latest", "Partners": "partners", "Halftime Draw": "ht-draw",
			"Additional Time": "additional", "Attendance": "attendance", "Team": "team",
			"General": "general", "Latest Scores": "latest-scores", "POTM": "potm"
		};
		const path = routes[screen];
		return path ? `/${environment.build.scoreboardRoute}/${path}` : null;
	}

	async claim() {
		const code = this.state.pairingCode.trim().toUpperCase();
		if (!code) return;
		this.setState({ saving: true });
		const res = await BoardService.claim(code, this.state.newName.trim() || "Scoreboard");
		this.setState({ saving: false });
		if (!res.ok) {
			this.toast.show({ closable: false, detail: "That code isn't valid — check the board and try again.", life: 5000, severity: "error", summary: "Couldn't link the board" });
			return;
		}
		this.toast.show({ closable: false, detail: "The scoreboard is now linked to your club.", life: 4000, severity: "success", summary: "Linked" });
		this.setState({ showAdd: false, pairingCode: "", newName: "" });
		this.getBoards();
	}

	async getLatestRelease() {
		try {
			const res = await safeJson(BoardService.latestRelease());
			if (res && !res.error) this.setState({ latestRelease: res });
		} catch { /* no release published */ }
	}

	/** A board is updatable when a release exists and the board isn't on it. */
	updateAvailable(board: Board) {
		const latest = this.state.latestRelease;
		return !!(latest?.version && latest?.url && board.appVersion !== latest.version);
	}

	confirmUpdate(board: Board) {
		const latest = this.state.latestRelease!;
		confirmDialog({
			accept: () => this.sendUpdate(board),
			header: "Update scoreboard",
			icon: "pi pi-cloud-download",
			message: `Update "${board.name}" from v${board.appVersion || "unknown"} to v${latest.version}? The board restarts itself on the new version — allow a minute or two.`
		});
	}

	async sendUpdate(board: Board) {
		const latest = this.state.latestRelease!;
		this.setState({ updating: { ...this.state.updating, [board.id]: true } });
		const value = JSON.stringify({ version: latest.version, url: latest.url, boardId: board.id });
		const res = await ScoreboardCommandService.sendEvent("Update", value, ClubContext.club?.id || 1);
		if (!res.ok) {
			this.setState({ updating: { ...this.state.updating, [board.id]: false } });
			this.toast.show({ closable: false, detail: "Please try again later.", life: 4000, severity: "error", summary: "Couldn't send the update" });
			return;
		}
		this.toast.show({ closable: false, detail: `"${board.name}" is downloading v${latest.version} and will restart itself.`, life: 6000, severity: "success", summary: "Update sent" });
		// Clear the spinner once the board reports the new version (or give up after 3 min).
		setTimeout(() => this.setState({ updating: { ...this.state.updating, [board.id]: false } }), 180000);
	}

	confirmUnpair(board: Board) {
		confirmDialog({
			accept: async () => {
				await BoardService.unpair(board.id);
				this.getBoards();
			},
			acceptClassName: "p-button-danger",
			header: "Remove scoreboard",
			icon: "pi pi-exclamation-triangle",
			message: `Remove "${board.name}"? It will show a new pairing code the next time it starts.`
		});
	}

	/** Named layouts, plus a "Club default" entry for unassigned boards. */
	layoutOptions() {
		return [{ id: null, name: "Club default" }, ...this.state.configs];
	}

	async getConfigs() {
		try {
			const res = await safeJson(ScoreboardConfigService.getMine());
			if (Array.isArray(res)) this.setState({ configs: res });
		} catch { /* pre-config API */ }
	}

	async assignLayout(board: Board, configId: number | null) {
		await ScoreboardConfigService.assign(board.id, configId);
		this.getBoards();
		this.toast.show({ closable: false, detail: "The board restyles the next time it refreshes its theme.", life: 4000, severity: "success", summary: "Layout assigned" });
	}

	async rename(board: Board, name: string) {
		if (!name.trim() || name === board.name) return;
		await BoardService.updateSingleById(board.id, { ...board, name: name.trim() });
		this.getBoards();
	}

	/** Live hub presence from the API — a connected board reports true. */
	isLive(board: Board) {
		return !!board.online;
	}

	lastSeenText(board: Board) {
		if (board.online) return "Online now";
		if (!board.lastSeenAt) return "Never connected";
		const seconds = Math.floor((Date.now() - Date.parse(board.lastSeenAt)) / 1000);
		if (seconds < 120) return "Seen moments ago";
		if (seconds < 3600) return `Last seen ${Math.floor(seconds / 60)} min ago`;
		if (seconds < 86400) return `Last seen ${Math.floor(seconds / 3600)} h ago`;
		return `Last seen ${new Date(board.lastSeenAt).toLocaleDateString("en-GB")}`;
	}

	render() {
		const { boards, isLoading } = this.state;
		return (
			<div style={{ padding: 20 }}>
				<Toast ref={(el) => (this.toast = el)} />
				<ConfirmDialog />

				<div style={{ alignItems: "center", display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
					<h2 style={{ margin: 0 }}>Scoreboards</h2>
					<Button icon="pi pi-plus" label="Add scoreboard" onClick={() => this.setState({ showAdd: true, pairingCode: "", newName: "" })} />
				</div>

				{isLoading && <div style={{ color: "#9DA3AF" }}>Loading…</div>}

				{!isLoading && boards.length === 0 &&
					<div className="boards-empty">
						<FontAwesomeIcon icon={faDisplay} />
						<h3>No scoreboards linked yet</h3>
						<p>Start the Pitchbase scoreboard app on your stadium PC. It will show a six-character code — click <strong>Add scoreboard</strong> and enter it here.</p>
					</div>}

				<div className="boards-grid">
					{boards.map((board) => (
						<div className="board-tile" key={`board-${board.id}`}>
							<div className="board-tile-head">
								<span className={`board-dot${this.isLive(board) ? " live" : ""}`} />
								<span className="board-head-actions">
									<button className={`board-refresh${this.state.refreshing[board.id] ? " spinning" : ""}`} onClick={() => this.refreshPreview(board.id)} title="Refresh preview" type="button">
										<FontAwesomeIcon icon={faArrowsRotate} />
									</button>
									<FontAwesomeIcon icon={faDisplay} />
								</span>
							</div>
							<BoardPreview
								fixture={this.state.fixture}
								homeBadge={ClubContext.club?.badgeUrl || "/assets/Badge.png"}
								screenRoute={(screen) => this.screenRoute(screen)}
								state={this.state.states[board.id] || null}
								theme={this.state.themes[board.scoreboardConfigId || 0] || this.state.themes[0]}
							/>
							<InputText className="board-name" defaultValue={board.name} onBlur={(e) => this.rename(board, e.target.value)} />
							<span className="board-seen">{this.lastSeenText(board)}</span>
							<div className="board-version">
								<span>Software v{board.appVersion || "unknown"}</span>
								{this.updateAvailable(board)
									? <Button className="p-button-sm" disabled={!this.isLive(board)} icon="pi pi-cloud-download" label={this.state.updating[board.id] ? "Updating…" : `Update to v${this.state.latestRelease!.version}`} loading={!!this.state.updating[board.id]} onClick={() => this.confirmUpdate(board)} title={this.isLive(board) ? undefined : "The board is offline"} />
									: this.state.latestRelease?.version ? <span className="board-up-to-date">Up to date</span> : null}
							</div>
							{this.state.configs.length > 0 &&
								<div className="board-layout">
									<label>Layout</label>
									<Dropdown optionLabel="name" optionValue="id" options={this.layoutOptions()} placeholder="Club default" style={{ width: "100%" }} value={board.scoreboardConfigId ?? null} onChange={(e) => this.assignLayout(board, e.value)} />
								</div>}
							<Button className="p-button-text p-button-danger board-remove" icon={<FontAwesomeIcon icon={faTrash} />} label="Remove" onClick={() => this.confirmUnpair(board)} />
						</div>
					))}
				</div>

				<Dialog footer={<>
					<Button className="p-button-text" label="Cancel" onClick={() => this.setState({ showAdd: false })} />
					<Button disabled={this.state.saving} icon="pi pi-check" label={this.state.saving ? "Linking…" : "Link scoreboard"} onClick={() => this.claim()} />
				</>} header="Add a scoreboard" modal onHide={() => this.setState({ showAdd: false })} style={{ width: 460 }} visible={this.state.showAdd}>
					<p style={{ color: "#9DA3AF", marginBottom: 18 }}>
						<FontAwesomeIcon icon={faLink} style={{ marginRight: 8 }} />
						Start the scoreboard app and type the code it displays.
					</p>
					<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Pairing code</label>
					<InputText autoFocus onChange={(e) => this.setState({ pairingCode: e.target.value.toUpperCase() })} placeholder="e.g. K4P7QX" style={{ fontSize: "1.4rem", letterSpacing: "0.25em", marginBottom: 16, textAlign: "center", textTransform: "uppercase", width: "100%" }} value={this.state.pairingCode} />
					<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Name</label>
					<InputText onChange={(e) => this.setState({ newName: e.target.value })} placeholder="Main LED" style={{ width: "100%" }} value={this.state.newName} />
				</Dialog>
			</div>
		);
	}
}
